import React, { useState, useEffect } from "react";
import axios from "axios";
import { 
  ClipboardCheck, 
  CheckCircle2, 
  AlertTriangle,
  Clock,
  FileText,
  Loader2,
  RefreshCw,
  Sparkles,
  Building2,
  Award,
  Briefcase,
  DollarSign,
  Shield,
  Bell,
  Calendar,
  User,
  ChevronDown,
  ArrowLeft,
  Save
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import RefreshButton from "@/components/v2/RefreshButton";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Compliance = () => {
  const [rfps, setRfps] = useState([]);
  const [selectedRfp, setSelectedRfp] = useState("");
  const [complianceData, setComplianceData] = useState({ items: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [autoChecking, setAutoChecking] = useState(false);
  
  // View state for inline editing (replaces modal)
  const [view, setView] = useState("list"); // list, edit
  const [editingItem, setEditingItem] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editForm, setEditForm] = useState({
    status: "pending",
    notes: "",
    reminder_date: "",
    assigned_to: ""
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchRfps = async () => {
      try {
        const response = await axios.get(`${API}/rfp/list`);
        const analyzedRfps = response.data.filter(r => r.status === "analyzed");
        setRfps(analyzedRfps);
        
        if (analyzedRfps.length > 0) {
          setSelectedRfp(analyzedRfps[0].id);
        }
      } catch (error) {
        console.error("Error fetching RFPs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRfps();
  }, []);

  useEffect(() => {
    if (selectedRfp) {
      fetchComplianceItems();
    }
  }, [selectedRfp]);

  const fetchComplianceItems = async () => {
    try {
      const response = await axios.get(`${API}/rfp/${selectedRfp}/compliance`);
      setComplianceData(response.data);
    } catch (error) {
      console.error("Error fetching compliance items:", error);
    }
  };

  const handleAutoCheck = async () => {
    setAutoChecking(true);
    try {
      await axios.post(`${API}/rfp/${selectedRfp}/compliance/auto-check`);
      await fetchComplianceItems();
      toast.success("Auto-check completed! Requirements matched against your company profile.");
    } catch (error) {
      toast.error("Auto-check failed");
    } finally {
      setAutoChecking(false);
    }
  };

  const handleQuickStatusChange = async (itemIndex, newStatus) => {
    try {
      await axios.put(`${API}/rfp/${selectedRfp}/compliance/${itemIndex}?status=${newStatus}`);
      await fetchComplianceItems();
      toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const openEditView = (item, index) => {
    setEditingItem(item);
    setEditingIndex(index);
    setEditForm({
      status: item.status || "pending",
      notes: item.notes || "",
      reminder_date: item.reminder_date || "",
      assigned_to: item.assigned_to || ""
    });
    setView("edit");
  };

  const goBack = () => {
    setView("list");
    setEditingItem(null);
    setEditingIndex(null);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const params = new URLSearchParams();
      params.append('status', editForm.status);
      if (editForm.notes) params.append('notes', editForm.notes);
      if (editForm.reminder_date) params.append('reminder_date', editForm.reminder_date);
      if (editForm.assigned_to) params.append('assigned_to', editForm.assigned_to);
      
      await axios.put(`${API}/rfp/${selectedRfp}/compliance/${editingIndex}?${params.toString()}`);
      await fetchComplianceItems();
      toast.success("Compliance item updated!");
      goBack();
    } catch (error) {
      toast.error("Failed to update item");
    } finally {
      setSaving(false);
    }
  };

  const handleReExtract = async () => {
    if (!selectedRfp) return;
    setAutoChecking(true);
    try {
      const res = await axios.post(`${API}/rfp/${selectedRfp}/compliance/re-extract`);
      await fetchComplianceItems();
      toast.success(`Re-extracted ${res.data.total_items} compliance requirements!`);
    } catch (error) {
      toast.error("Failed to re-extract requirements");
    } finally {
      setAutoChecking(false);
    }
  };

  const getFilteredItems = () => {
    let items = complianceData.items || [];
    if (statusFilter !== "all") {
      items = items.filter(item => item.status === statusFilter);
    }
    if (categoryFilter !== "all") {
      items = items.filter(item => item.category === categoryFilter);
    }
    return items;
  };

  const stats = complianceData.stats || {};
  const totalItems = stats.total || 0;
  const completionRate = totalItems > 0 
    ? Math.round(((stats.met || 0) / totalItems) * 100) 
    : 0;

  const getCategoryIcon = (category) => {
    switch (category) {
      case "certification": return <Award className="w-4 h-4" />;
      case "experience": return <Briefcase className="w-4 h-4" />;
      case "financial": return <DollarSign className="w-4 h-4" />;
      case "technical": return <Shield className="w-4 h-4" />;
      case "registration": return <Building2 className="w-4 h-4" />;
      default: return <ClipboardCheck className="w-4 h-4" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case "certification": return "bg-purple-100 text-purple-700";
      case "experience": return "bg-blue-100 text-blue-700";
      case "financial": return "bg-emerald-100 text-emerald-700";
      case "technical": return "bg-indigo-100 text-indigo-700";
      case "mandatory": return "bg-red-100 text-red-700";
      case "eligibility": return "bg-amber-100 text-amber-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  const isOverdue = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen" data-testid="compliance-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#007AFF]" />
      </div>
    );
  }

  // Edit View (replaces modal)
  if (view === "edit" && editingItem) {
    return (
      <div className="p-8 max-w-2xl mx-auto" data-testid="compliance-edit-view">
        <Button variant="outline" onClick={goBack} className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Compliance List
        </Button>

        <Card className="border-[#E2E8F0]">
          <CardHeader>
            <CardTitle className="font-['Manrope'] flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-[#007AFF]" />
              Edit Compliance Item
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Requirement Text */}
            <div className="p-4 bg-[#F1F5F9] rounded-lg">
              <p className="text-sm text-[#64748B] mb-1">Requirement</p>
              <p className="text-[#0F172A]">{editingItem.requirement}</p>
              {editingItem.category && (
                <Badge className={`mt-2 ${getCategoryColor(editingItem.category)}`}>
                  {getCategoryIcon(editingItem.category)}
                  <span className="ml-1">{editingItem.category}</span>
                </Badge>
              )}
            </div>

            {/* Status Selection */}
            <div className="space-y-2">
              <Label className="font-semibold">Status</Label>
              <div className="flex gap-2 flex-wrap">
                <Button
                  type="button"
                  variant={editForm.status === "pending" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setEditForm({ ...editForm, status: "pending" })}
                  className={editForm.status === "pending" ? "bg-amber-600 hover:bg-amber-700" : ""}
                  data-testid="status-pending-btn"
                >
                  <Clock className="w-4 h-4 mr-1" />
                  Pending
                </Button>
                <Button
                  type="button"
                  variant={editForm.status === "met" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setEditForm({ ...editForm, status: "met" })}
                  className={editForm.status === "met" ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                  data-testid="status-met-btn"
                >
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  Met
                </Button>
                <Button
                  type="button"
                  variant={editForm.status === "not_met" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setEditForm({ ...editForm, status: "not_met" })}
                  className={editForm.status === "not_met" ? "bg-red-600 hover:bg-red-700" : ""}
                  data-testid="status-not-met-btn"
                >
                  <AlertTriangle className="w-4 h-4 mr-1" />
                  Not Met
                </Button>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes" className="font-semibold">Notes</Label>
              <Textarea
                id="notes"
                placeholder="Add notes about this requirement..."
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                rows={3}
                data-testid="notes-textarea"
              />
            </div>

            {/* Reminder Date */}
            <div className="space-y-2">
              <Label htmlFor="reminder" className="font-semibold flex items-center gap-2">
                <Bell className="w-4 h-4" />
                Set Reminder
              </Label>
              <Input
                id="reminder"
                type="date"
                value={editForm.reminder_date}
                onChange={(e) => setEditForm({ ...editForm, reminder_date: e.target.value })}
                data-testid="reminder-input"
              />
            </div>

            {/* Assigned To */}
            <div className="space-y-2">
              <Label htmlFor="assigned" className="font-semibold flex items-center gap-2">
                <User className="w-4 h-4" />
                Assign To
              </Label>
              <Input
                id="assigned"
                placeholder="Team member name or department..."
                value={editForm.assigned_to}
                onChange={(e) => setEditForm({ ...editForm, assigned_to: e.target.value })}
                data-testid="assigned-input"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-[#E2E8F0]">
              <Button variant="outline" onClick={goBack} data-testid="cancel-edit-btn">
                Cancel
              </Button>
              <Button onClick={handleSaveEdit} disabled={saving} className="bg-[#007AFF] hover:bg-[#0066DD]" data-testid="save-edit-btn">
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Changes
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // List View
  return (
    <div className="p-8" data-testid="compliance-page">
      {/* Header */}
      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-[#0F172A] font-['Manrope'] tracking-tight">
            Compliance Tracker
          </h1>
          <p className="text-[#64748B] mt-1">
            Track eligibility criteria and compliance requirements - auto-matched against your company profile
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={handleReExtract}
            disabled={!selectedRfp || autoChecking}
            data-testid="re-extract-btn"
          >
            {autoChecking ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Re-Extract
          </Button>
          <Button
            variant="outline"
            onClick={handleAutoCheck}
            disabled={!selectedRfp || autoChecking}
            data-testid="auto-check-btn"
          >
            {autoChecking ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            Auto-Check Profile
          </Button>
          <RefreshButton onClick={fetchComplianceItems} data-testid="refresh-compliance-btn" />
        </div>
      </div>

      {/* RFP Selection */}
      <div className="mb-6">
        <Select value={selectedRfp} onValueChange={setSelectedRfp}>
          <SelectTrigger className="w-[400px]" data-testid="compliance-rfp-select">
            <SelectValue placeholder="Select an RFP" />
          </SelectTrigger>
          <SelectContent>
            {rfps.length === 0 ? (
              <div className="p-4 text-center text-sm text-[#64748B]">
                No analyzed RFPs available
              </div>
            ) : (
              rfps.map((rfp) => (
                <SelectItem key={rfp.id} value={rfp.id}>
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    {rfp.name}
                  </div>
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>

      {!selectedRfp ? (
        <Card className="border-[#E2E8F0]">
          <CardContent className="p-12 text-center">
            <ClipboardCheck className="w-16 h-16 mx-auto text-[#E2E8F0] mb-4" />
            <p className="text-lg font-semibold text-[#0F172A] mb-2">
              Select an RFP to view compliance items
            </p>
            <p className="text-[#64748B]">
              Upload and analyze an RFP document first to track compliance requirements
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
            <Card className="border-[#E2E8F0]" data-testid="stat-total-items">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-[#64748B] mb-1">Total</p>
                    <p className="text-3xl font-extrabold font-['Manrope'] text-[#0F172A]">
                      {totalItems}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-[#007AFF]/10 rounded-xl flex items-center justify-center">
                    <ClipboardCheck className="w-6 h-6 text-[#007AFF]" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#E2E8F0]" data-testid="stat-pending">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-[#64748B] mb-1">Pending</p>
                    <p className="text-3xl font-extrabold font-['Manrope'] text-amber-600">
                      {stats.pending || 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Clock className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#E2E8F0]" data-testid="stat-met">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-[#64748B] mb-1">Met</p>
                    <p className="text-3xl font-extrabold font-['Manrope'] text-emerald-600">
                      {stats.met || 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#E2E8F0]" data-testid="stat-not-met">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-[#64748B] mb-1">Not Met</p>
                    <p className="text-3xl font-extrabold font-['Manrope'] text-red-600">
                      {stats.not_met || 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#E2E8F0]" data-testid="stat-auto-matched">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-[#64748B] mb-1">Auto-Matched</p>
                    <p className="text-3xl font-extrabold font-['Manrope'] text-purple-600">
                      {stats.auto_matched || 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Completion Progress */}
          <Card className="border-[#E2E8F0] mb-6" data-testid="completion-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-['Manrope'] font-bold text-lg text-[#0F172A]">
                    Compliance Completion
                  </h3>
                  <p className="text-sm text-[#64748B]">
                    {stats.met || 0} of {totalItems} requirements verified as met
                  </p>
                </div>
                <span className="text-3xl font-extrabold font-['Manrope'] text-[#007AFF]">
                  {completionRate}%
                </span>
              </div>
              <Progress value={completionRate} className="h-3" />
            </CardContent>
          </Card>

          {/* Filter and Items */}
          <Card className="border-[#E2E8F0]" data-testid="compliance-items-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-['Manrope'] text-lg font-bold">
                  Compliance Requirements
                </CardTitle>
                <CardDescription>
                  Click on any item to edit status, add notes, or set a reminder
                </CardDescription>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="mandatory">Mandatory</SelectItem>
                    <SelectItem value="eligibility">Eligibility</SelectItem>
                    <SelectItem value="certification">Certification</SelectItem>
                    <SelectItem value="experience">Experience</SelectItem>
                    <SelectItem value="financial">Financial</SelectItem>
                    <SelectItem value="technical">Technical</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant={statusFilter === "all" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStatusFilter("all")}
                  className={statusFilter === "all" ? "bg-[#0F172A]" : ""}
                >
                  All
                </Button>
                <Button
                  variant={statusFilter === "pending" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStatusFilter("pending")}
                  className={statusFilter === "pending" ? "bg-amber-600 hover:bg-amber-700" : ""}
                >
                  <Clock className="w-4 h-4 mr-1" />
                  Pending
                </Button>
                <Button
                  variant={statusFilter === "met" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStatusFilter("met")}
                  className={statusFilter === "met" ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                >
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  Met
                </Button>
                <Button
                  variant={statusFilter === "not_met" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStatusFilter("not_met")}
                  className={statusFilter === "not_met" ? "bg-red-600 hover:bg-red-700" : ""}
                >
                  <AlertTriangle className="w-4 h-4 mr-1" />
                  Not Met
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {getFilteredItems().length === 0 ? (
                <div className="text-center py-12">
                  <ClipboardCheck className="w-16 h-16 mx-auto text-[#E2E8F0] mb-4" />
                  <p className="text-lg font-semibold text-[#0F172A] mb-2">
                    {totalItems === 0 
                      ? "No compliance items detected" 
                      : "No items match the selected filters"}
                  </p>
                  <p className="text-[#64748B]">
                    {totalItems === 0 
                      ? "The AI extracts requirements like certifications, experience, turnover during RFP analysis"
                      : "Try selecting different filters"}
                  </p>
                </div>
              ) : (
                <div className="h-[500px] overflow-y-auto pr-2" data-testid="compliance-scroll-container">
                  <div className="space-y-3">
                    {getFilteredItems().map((item, idx) => (
                      <div 
                        key={idx}
                        className="flex items-start gap-4 p-4 bg-[#F1F5F9] rounded-lg hover:bg-[#E2E8F0] transition-colors cursor-pointer group"
                        onClick={() => openEditView(item, idx)}
                        data-testid={`compliance-item-${idx}`}
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                          item.status === "met" ? "bg-emerald-100" :
                          item.status === "not_met" ? "bg-red-100" :
                          "bg-amber-100"
                        }`}>
                          {item.status === "met" ? (
                            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                          ) : item.status === "not_met" ? (
                            <AlertTriangle className="w-5 h-5 text-red-600" />
                          ) : (
                            <Clock className="w-5 h-5 text-amber-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-[#0F172A] leading-relaxed">
                            {item.requirement}
                          </p>
                          {item.notes && (
                            <p className="text-xs text-emerald-600 mt-1 font-medium">
                              Note: {item.notes}
                            </p>
                          )}
                          <div className="flex gap-2 mt-3 flex-wrap items-center">
                            {item.category && (
                              <Badge className={`text-xs ${getCategoryColor(item.category)}`}>
                                {getCategoryIcon(item.category)}
                                <span className="ml-1">{item.category}</span>
                              </Badge>
                            )}
                            {item.priority === "high" && (
                              <Badge className="text-xs bg-red-100 text-red-700">
                                High Priority
                              </Badge>
                            )}
                            {item.auto_matched && (
                              <Badge className="text-xs bg-purple-100 text-purple-700">
                                <Sparkles className="w-3 h-3 mr-1" />
                                Auto-Matched
                              </Badge>
                            )}
                            {item.reminder_date && (
                              <Badge className={`text-xs ${isOverdue(item.reminder_date) ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                                <Bell className="w-3 h-3 mr-1" />
                                {formatDate(item.reminder_date)}
                                {isOverdue(item.reminder_date) && " (Overdue)"}
                              </Badge>
                            )}
                            {item.assigned_to && (
                              <Badge className="text-xs bg-gray-100 text-gray-700">
                                <User className="w-3 h-3 mr-1" />
                                {item.assigned_to}
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        {/* Quick Status Buttons */}
                        <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="outline" size="sm" className="h-8">
                                Status <ChevronDown className="w-3 h-3 ml-1" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleQuickStatusChange(idx, "pending")}>
                                <Clock className="w-4 h-4 mr-2 text-amber-600" />
                                Pending
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleQuickStatusChange(idx, "met")}>
                                <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-600" />
                                Met
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleQuickStatusChange(idx, "not_met")}>
                                <AlertTriangle className="w-4 h-4 mr-2 text-red-600" />
                                Not Met
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default Compliance;
